<!-- components/ProductRecommendations.vue -->
<template>
  <div class="product-recommendations">
	<h2>{{ $t('recommendations.heading') }}</h2>
	<p>{{ $t('recommendations.subheading') }}</p>

	<div v-if="loading" class="loading">{{ $t('recommendations.loading') }}</div>
	<div v-else-if="error" class="error">{{ error }}</div>
	<div v-else-if="recommendations.length === 0" class="no-recommendations">
	  {{ $t('recommendations.no_recommendations') }}
	</div>
	
	<div v-else class="recommendations-grid">
	  <div v-for="product in recommendations" :key="product.id" class="product-card">
		<h3>{{ translateProductName(product.name) }}</h3>
		<div v-if="product.image_url" class="product-image">
		  <img :src="product.image_url" :alt="translateProductName(product.name)" />
		</div>
		<a v-if="product.url"
		   :href="product.url"
		   target="_blank"
		   class="learn-more-btn">
		  {{ $t('recommendations.learn_more') }}
		</a>
	  </div>
	</div>
  </div>
</template>

<script>
import axios from 'axios';

export default {
  name: 'ProductRecommendations',
  props: {
	assessment: {
	  type: Object,
	  required: true
	},
	questions: {
	  type: Array,
	  required: true
	},
	userType: {
	  type: String,
	  required: true
	}
  },
  data() {
	return {
	  recommendations: [],
	  categoryGaps: {},
	  loading: false,
	  error: null,
	  // Map English product names to translation keys
	  productNameKeys: {
		'Dunnage Rack': 'results.dunnage_rack',
		'Dunnage Racks': 'results.dunnage_racks',
		'Sanitizer Only Personalized Shakers': 'results.sanitizer_only_personalized_shakers',
		'Camdolly': 'results.camdolly',
		'Service Cart Pro': 'results.service_cart_pro',
		'Cambro Food Storage': 'results.cambro_food_storage',
		'StoreSafe Food Rotation Labels': 'results.storesafe_food_rotation_labels',
		'Seal Lid': 'results.seal_lid',
		'Seal Lids': 'results.seal_lids',
		'Universal Storage Rack': 'results.universal_storage_rack',
		'Food Storage': 'results.food_storage',
		'Camshelving': 'results.camshelving',
		'Camshelving Accessories (#10 Can Rack, Universal Rack)': 'results.camshelving_accessories_10_can_rack_universal',
		'Camshelving Accessories': 'results.camshelving_accessories',
		'ID Tags': 'results.id_tags',
		'CamSquares FreshPro 16 oz. & 32 oz.': 'results.camsquares_freshpro_16_oz_32_oz',
		'CamSquares FreshPro': 'results.camsquares_freshpro',
		'Camwear Food Pans': 'results.camwear_food_pans',
		'High Heat Food Pans': 'results.high_heat_food_pans',
		'Camwear Scoops': 'results.camwear_scoops',
		'4, 6 & 8 qt Food Storage': 'results.4_6_8_qt_food_storage',
		'Sanitizer Personalized Rounds': 'results.sanitizer_personalized_rounds',
		'Full Size & Half Size 2-inch Deep High Heat Pans': 'results.full_size_half_size_2inch_deep',
		'Ultimate Sheet Pan Rack': 'results.ultimate_sheet_pan_rack',
		'Utimate Sheet Pan Rack': 'results.utimate_sheet_pan_rack',
		'GN Food Pan Trolley': 'results.gn_food_pan_trolley',
		'Pro Cart Ultra': 'results.pro_cart_ultra',
		'Cambro Ladles': 'results.cambro_ladles',
		'Personalized Cambro Ladles': 'results.personalized_cambro_ladles',
		'Serving Spoons': 'results.serving_spoons',
		'Tongs': 'results.tongs',
		'GripLid': 'results.griplid',
		'EPP': 'results.epp',
		'UPC': 'results.upc',
		'Vertical Drying Rack': 'results.vertical_drying_rack',
		'Wall Shelving Sink Station': 'results.wall_shelving_sink_station',
		'Adjustable Dish Caddy': 'results.adjustable_dish_caddy',
		'Vented Shelf Plates': 'results.vented_shelf_plates',
		'Divider Bars': 'results.divider_bars',
		'Ice Caddy': 'results.ice_caddy',
		'Poly Cambox': 'results.poly_cambox',
		'Pegboard': 'results.pegboard'
	  }
	};
  },
  mounted() {
	this.fetchRecommendations();
  },
  methods: {
	translateProductName(name) {
	  // Look up the translation key for this product name
	  const key = this.productNameKeys[name];
	  if (key) {
		const translated = this.$t(key);
		// If translation exists and isn't the key itself, use it
		if (translated && translated !== key) {
		  return translated;
		}
	  }
	  // Fallback to original name if no translation found
	  return name;
	},
	async fetchRecommendations() {
	  this.loading = true;
	  this.error = null;
	  
	  try {
		const response = await axios.post(
		  `/api/recommendations/assessment/${this.assessment.id}`,
		  {
			assessment: this.assessment,
			questions: this.questions,
			userType: this.userType
		  },
		  {
			withCredentials: true,
			baseURL: process.env.VUE_APP_API_URL || 'https://cambro.fyi'
		  }
		);
		
		this.recommendations = response.data.recommendations;
		this.categoryGaps = response.data.categoryGaps;
		
		console.log('Recommendations loaded:', this.recommendations.length);
	  } catch (error) {
		console.error('Error fetching recommendations:', error);
		this.error = this.$t('recommendations.error_loading');
	  } finally {
		this.loading = false;
	  }
	}
  }
};
</script>

<style scoped>
.product-recommendations {
  margin: 40px 0;
  padding: 20px;
  background: white;
  border-radius: 8px;
  border: 1px solid #ddd;
}

.product-recommendations h2 {
  color: #EF3E42;
  text-align: center;
  margin-bottom: 10px;
}

.product-recommendations p {
  text-align: center;
  color: #666;
  margin-bottom: 30px;
}

.recommendations-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 20px;
}

.product-card {
  border: 2px solid #000;
  border-radius: 8px;
  padding: 20px;
  background: white;
  text-align: center;
}

.product-card h3 {
  color: #EF3E42;
  margin-bottom: 15px;
  font-size: 18px;
  font-weight: 600;
}

.product-scores {
  margin-bottom: 15px;
}

.score-item {
  display: flex;
  justify-content: space-between;
  margin-bottom: 8px;
  font-size: 14px;
}

.score-label {
  color: #666;
}

.score-value {
  font-weight: bold;
  color: #333;
}

.relevance-score {
  font-size: 12px;
  color: #999;
  margin-bottom: 15px;
  font-style: italic;
}

.learn-more-btn {
  display: inline-block;
  background: #EF3E42;
  color: white;
  padding: 8px 16px;
  text-decoration: none;
  border-radius: 4px;
  font-weight: 600;
  text-transform: uppercase;
  font-size: 14px;
}

.learn-more-btn:hover {
  background: #c41c1f;
  color: white;
}

.loading, .error, .no-recommendations {
  text-align: center;
  padding: 20px;
  color: #666;
}

.error {
  color: #e74c3c;
}

@media (max-width: 768px) {
  .recommendations-grid {
	grid-template-columns: 1fr;
  }
  
  .product-card {
	padding: 15px;
  }
}
@media print {
  .product-recommendations {
	margin: 20px 0;
	padding: 15px;
	page-break-inside: avoid;
	break-inside: avoid;
  }
  
  .recommendations-grid {
	display: flex !important;
	flex-wrap: wrap !important;
	justify-content: space-between !important;
	gap: 15px !important;
	max-width: 100% !important;
  }
  
  .product-card {
	width: 200px !important;
	min-width: 200px !important;
	max-width: 200px !important;
	margin: 0 !important;
	padding: 15px !important;
	page-break-inside: avoid !important;
	break-inside: avoid !important;
	box-sizing: border-box !important;
  }
  
  .product-card h3 {
	font-size: 14px !important;
	margin-bottom: 10px !important;
  }
  
  .product-image {
	height: 100px !important;
	margin-bottom: 10px !important;
  }
  
  .learn-more-btn {
	font-size: 10px !important;
	padding: 6px 12px !important;
  }
}

/* Force layout for PDF generation libraries */
.pdf-export-mode .recommendations-grid {
  display: flex !important;
  flex-wrap: wrap !important;
  justify-content: flex-start !important;
  gap: 15px !important;
}

.pdf-export-mode .product-card {
  width: 200px !important;
  min-width: 200px !important;
  max-width: 200px !important;
  flex: none !important;
}
</style>